import React from 'react';
import { useNavigate } from 'react-router-dom';
import ServiceLayout from '../../components/ServiceLayout';
import { useMetrics } from '../../hooks/useMetrics';
import { 
  ChatBubbleBottomCenterTextIcon,
  BoltIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  StarIcon,
  CpuChipIcon,
  PhoneIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const Helpdesk: React.FC = () => {
  const navigate = useNavigate();
  const { metrics, loading } = useMetrics();

  const features = [
    {
      icon: <PhoneIcon />,
      title: 'Multi-Channel Support',
      description: 'Get help through phone, email, chat, or ticket system - whatever works best for you.',
      benefits: [
        '24/7 phone support availability',
        'Email and web portal access',
        'Live chat for immediate assistance',
        'Ticket tracking and management',
        'Mobile-friendly support options'
      ]
    },
    {
      icon: <BoltIcon />,
      title: 'Rapid Response Times',
      description: 'Fast resolution of your IT issues with guaranteed response times based on priority.',
      benefits: [
        'Emergency response within 15 minutes',
        'High priority within 2 hours',
        'Standard issues within 24 hours',
        'Proactive issue identification',
        'Status updates and communication'
      ]
    },
    {
      icon: <UserGroupIcon />,
      title: 'Expert Technical Team',
      description: 'Certified IT professionals with extensive experience across all major technologies.',
      benefits: [
        'Microsoft certified technicians',
        'Cloud platform specialists',
        'Network and security experts',
        'Software application support',
        'Hardware troubleshooting'
      ]
    },
    {
      icon: <CheckBadgeIcon />,
      title: 'Service Level Guarantees',
      description: 'Committed service levels with clear metrics and performance guarantees.',
      benefits: [
        'Defined response time SLAs',
        'Resolution time commitments',
        'Uptime guarantees',
        'Customer satisfaction tracking',
        'Service credit policies'
      ]
    },
    {
      icon: <CpuChipIcon />,
      title: 'Remote & On-Site Support',
      description: 'Flexible support delivery options to meet your specific needs and preferences.',
      benefits: [
        'Remote desktop troubleshooting',
        'On-site technical visits',
        'Emergency dispatch services',
        'Hardware replacement coordination',
        'Installation and setup support'
      ]
    },
    {
      icon: <StarIcon />,
      title: 'Proactive Monitoring',
      description: 'Continuous monitoring to identify and resolve issues before they impact your business.',
      benefits: [
        'System health monitoring',
        'Performance threshold alerts',
        'Preventive maintenance',
        'Security threat monitoring',
        'Capacity planning insights'
      ]
    }
  ];

  const process = {
    title: 'Our Support Process',
    description: 'Streamlined support workflow designed to get you back up and running as quickly as possible.',
    steps: [
      {
        title: 'Issue Identification',
        description: 'Contact us through your preferred channel and we\'ll immediately assess the issue priority and impact on your business.'
      },
      {
        title: 'Initial Response & Triage',
        description: 'Our technicians provide immediate response within SLA timeframes and determine the best resolution approach.'
      },
      {
        title: 'Diagnosis & Troubleshooting',
        description: 'Systematic diagnosis using advanced tools and expertise to identify the root cause of the problem.'
      },
      {
        title: 'Resolution & Testing',
        description: 'Implement the solution and conduct thorough testing to ensure the issue is completely resolved.'
      },
      {
        title: 'Follow-up & Documentation',
        description: 'Confirm resolution satisfaction and document the solution for future reference and knowledge sharing.'
      }
    ]
  };

  const stats = [
    { value: metrics?.active_clients_display || '25+', label: 'Active Clients' },
    { value: metrics?.response_time_display || '<15min', label: 'Response Time' },
    { value: metrics?.resolution_rate_display || '94%', label: 'First-Call Resolution' }
  ];

  const cta = {
    title: 'Need IT Support Right Now?',
    description: 'Our expert technicians are standing by to help resolve your IT issues quickly and efficiently.',
    buttonText: 'Get Support Now',
    onAction: () => navigate('/contact')
  };

  return (
    <ServiceLayout
      serviceName="Helpdesk"
      icon={<ChatBubbleBottomCenterTextIcon />}
      title="Expert IT Support When You Need It"
      subtitle="Get fast, reliable IT support from certified technicians who understand your business. Our comprehensive helpdesk services ensure your team stays productive with minimal downtime and maximum satisfaction."
      heroImage="/images/services/helpdesk-support.webp"
      stats={stats}
      features={features}
      process={process}
      cta={cta}
    />
  );
};

export default Helpdesk;